import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Typography,
  IconButton,
  Grid2,
  Divider,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Breadcrumbs,
  Link,
  Button,
} from '@mui/material';
import { Delete, Download, Folder, MoreVert, OpenInNew, RestoreFromTrash } from '@mui/icons-material';
import FileIcon from '../fileIcon';

export interface LibraryItem {
  id: string;
  name: string;
  type: 'folder' | 'file';
  lastModified?: Date;
  size?: string;
  status?: boolean;
  urlFile?: string;
}

export interface ItemProps {
  item: LibraryItem;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export const DestakItem: React.FC<ItemProps> = ({ item, onClick, onContextMenu }) => {
  return <CardActionArea
    sx={{ flexGrow: 1 }}
    onClick={() => item.type === "folder" && onClick()}
    onContextMenu={onContextMenu}
  >
    <Card sx={{
      gap: "1em",
      flexGrow: 1,
      display: "flex",
      padding: "0.5em",
      flexDirection: "row",
      alignItems: "center",
    }} >
      {item.type === "folder" && <Folder fontSize="small" />}
      {item.type === "file" && <div onClick={() => item.type === "file" && onClick()}>
        <FileIcon extension={item.name.split(".")[1] ?? ""} />
      </div>}
      <Box
        onClick={() => item.type === "file" && onClick()}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography
          fontSize={12}
          sx={{
            fontWeight: 450,
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            maxHeight: '2.8em',
            lineHeight: '1.4em'
          }}
        >
          {item.name}
        </Typography>
        <Typography
          fontSize={10}
          color="textSecondary">
          {item.lastModified && item.lastModified.toLocaleDateString()}
          {item.lastModified && " " + item.lastModified.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          {item.size && ` • ${item.size}`}
        </Typography>
      </Box>
      {item.type === "folder" && <OpenInNew fontSize="small" />}
      {item.type === "file" && <Download fontSize="small" />}
    </Card>
  </CardActionArea >;
};

export const Item: React.FC<ItemProps> = ({ item, onClick, onContextMenu }) => {
  return <CardActionArea
    sx={{ flexGrow: 1, maxHeight: "4em" }}
    onClick={onClick}
    onContextMenu={onContextMenu}
  >
    <Box sx={{
      gap: "0.5em",
      display: "flex",
      flexDirection: "row",
      paddingY: "0.5em",
      alignItems: "center",
    }}>
      {item.type === "folder" && <Folder fontSize="small" />}
      {item.type === "file" && <FileIcon extension={item.name.split(".")[1] ?? ""} />}

      <div style={{ flex: 1 }}>
        <Typography
          fontSize={12}
          sx={{
            fontWeight: 450,
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            maxHeight: '2.8em',
            lineHeight: '1.4em'
          }}
        >
          {item.name}
        </Typography>
      </div>

      <Typography
        fontSize={12}
        sx={{
          fontWeight: 450,
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          wordBreak: 'break-word',
          maxHeight: '2.8em',
          lineHeight: '1.4em'
        }}
      >
        {item.lastModified && item.lastModified.toLocaleDateString()}
        {item.lastModified && " " + item.lastModified.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Typography>
    </Box>
  </CardActionArea >;
};

interface LibraryFolderViewProps {
  title?: string;
  titleBradcrumb?: boolean;
  destakItems?: LibraryItem[];
  showTrash?: boolean;
  items: LibraryItem[];
  onLoadMore?: () => void;
  onBradcrumbClick?: (paths: string[]) => void;
  onExclude?: (item: LibraryItem) => void;
  onRestore?: (item: LibraryItem) => void;
  onTrashClick?: () => void;
  onItemClick?: (item: LibraryItem) => void;
}

export const LibraryFolderView: React.FC<LibraryFolderViewProps> = ({
  title,
  items,
  showTrash,
  destakItems,
  titleBradcrumb,
  onExclude,
  onRestore,
  onLoadMore,
  onItemClick,
  onTrashClick,
  onBradcrumbClick,
}) => {
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: LibraryItem | null;
  } | null>(null);

  const handleContextMenu = (event: React.MouseEvent, item: LibraryItem) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
          mouseX: event.clientX - 2,
          mouseY: event.clientY - 4,
          item
        }
        : null
    );
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleDelete = () => {
    if (onExclude && contextMenu?.item) onExclude(contextMenu.item);
    handleClose();
  };

  const handleRestore = () => {
    if (onRestore && contextMenu?.item) onRestore(contextMenu.item);
    handleClose();
  };

  return (<>
    {/* Menu de contexto */}
    <Menu
      open={contextMenu !== null}
      onClose={handleClose}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      {(contextMenu?.item?.status ?? true) ? (
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <Typography variant="inherit">Excluir</Typography>
        </MenuItem>
      ) : (
        <MenuItem onClick={handleRestore}>
          <ListItemIcon>
            <RestoreFromTrash fontSize="small" color="action" />
          </ListItemIcon>
          <Typography variant="inherit">Restaurar</Typography>
        </MenuItem>
      )}
    </Menu>

    {/* Biblioteca */}
    <Box sx={{ flex: 1, display: 'flex', height: '100%', overflow: 'auto', gap: "0.5em", flexDirection: "column" }}>

      {/* Título, botão da lixeira ou Breadcrumbs */}
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ flex: 1 }} >
          {!titleBradcrumb && <Typography fontWeight={550} fontSize={14} color='secondary.main' sx={{ px: "0.5em", pt: "0.5em" }}>
            {title ?? "Biblioteca da Barragem"}
          </Typography>}

          {titleBradcrumb && <Breadcrumbs>
            {(title ?? "Biblioteca da Barragem").split("/").map((val, i) => {
              return <Link key={i} underline="hover" fontWeight={550} fontSize={14} color='secondary.main' onClick={() => {
                if (onBradcrumbClick) onBradcrumbClick((title ?? "Biblioteca da Barragem").split("/").slice(0, i + 1));
              }}>
                {val}
              </Link >;
            })}
          </Breadcrumbs>}
        </div>
        {showTrash &&
          <Tooltip title={"Lixeira"}>
            <IconButton onClick={onTrashClick}>
              <Delete fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        }
      </div>
      <Divider />

      {/* Items em destaque */}
      <Grid2 container spacing={"0.5em"}>
        {destakItems?.map((item) => (
          <Grid2 key={item.id} size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <DestakItem
              item={item}
              onClick={() => onItemClick && onItemClick(item)}
              onContextMenu={(e) => item.type === "file" && handleContextMenu(e, item)}
            />
          </Grid2>
        ))}

      </Grid2>

      {/* Demais itens */}
      <Box sx={{
        flex: 1,
        height: "100%",
        padding: "0.5em",
        display: "flex",
        flexDirection: "column",
      }}>
        {/* Título */}
        <Box sx={{
          display: "flex",
          flexDirection: "row",
        }}>
          <div style={{ flex: 1 }}>
            <Typography fontWeight={350} fontSize={14}>
              Nome
            </Typography>
          </div>
          <Typography fontWeight={350} fontSize={14}>
            Última Modificação
          </Typography>
        </Box>

        {/* Divisor */}
        <Divider sx={{ backgroundColor: "primary.main" }} />

        {/* Item sem Destaque */}
        {items.map((item) => <Item
          key={item.id}
          item={item}
          onClick={() => onItemClick && onItemClick(item)}
          onContextMenu={(e) => item.type === "file" && handleContextMenu(e, item)}
        />)}
      </Box>

      {/* Load more */}
      {onLoadMore && <Breadcrumbs><Link underline="hover" fontWeight={550} fontSize={14} onClick={onLoadMore}>Carregar Mais</Link></Breadcrumbs>}
    </Box >
  </>
  );
};